import { Box, Paper, Popper, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

// ─── Layout constants ──────────────────────────────────────────────────────────
export const CELL_WIDTH  = 120;
export const ROW_HEIGHT  = 56;
export const LEFT_COL    = 80;
export const HOURS       = Array.from({ length: 24 }, (_, i) => i);

// ─── Root ──────────────────────────────────────────────────────────────────────
export const GuideRoot = styled(Box)(({ theme, $width, $height }) => ({
  position: "relative",
  overflow: "hidden",
  border: `1px solid ${theme.palette.borderColor.dark}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.default,
  width: $width,
  height: $height,
}));

// ─── Top-left corner ──────────────────────────────────────────────────────────
export const CornerBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: LEFT_COL,
  height: ROW_HEIGHT,
  zIndex: 20,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.borderColor.dark}`,
  borderBottom: `1px solid ${theme.palette.borderColor.dark}`,
}));

// ─── Hour header ──────────────────────────────────────────────────────────────
export const HeaderScroller = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: LEFT_COL,
  right: 0,
  height: ROW_HEIGHT,
  zIndex: 15,
  backgroundColor: theme.palette.background.paper,
  borderBottom: `1px solid ${theme.palette.borderColor.dark}`,
  overflowX: "auto",
  overflowY: "hidden",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

export const HeaderInner = styled(Box)(() => ({
  display: "flex",
  width: CELL_WIDTH * 24,
}));

export const HourCell = styled(Box)(({ theme }) => ({
  width: CELL_WIDTH,
  flexShrink: 0,
  height: ROW_HEIGHT,
  borderRight: `1px solid ${theme.palette.borderColor.default}`,
  display: "flex",
  alignItems: "center",
  paddingLeft: 10,
  paddingRight: 10,
  position: "relative",
}));

export const HalfHourTick = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: CELL_WIDTH / 2 - 0.5,
  bottom: 0,
  width: 1,
  height: "25%",
  backgroundColor: theme.palette.borderColor.default,
}));

// ─── Day column ────────────────────────────────────────────────────────────────
export const DayScroller = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: ROW_HEIGHT,
  left: 0,
  bottom: 0,
  width: LEFT_COL,
  zIndex: 15,
  backgroundColor: theme.palette.background.paper,
  borderRight: `1px solid ${theme.palette.borderColor.dark}`,
  overflowY: "auto",
  overflowX: "hidden",
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
}));

export const DayInner = styled(Box)(() => ({
  height: ROW_HEIGHT * 7,
}));

export const DayRowBox = styled(Box)(({ theme, $isToday }) => ({
  height: ROW_HEIGHT,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  borderBottom: `1px solid ${theme.palette.borderColor.default}`,
  backgroundColor: $isToday ? theme.palette.background.secondary : "transparent",
  gap: 2,
}));

// ─── Main scrollable grid ──────────────────────────────────────────────────────
export const MainScroller = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: ROW_HEIGHT,
  left: LEFT_COL,
  right: 0,
  bottom: 0,
  overflow: "auto",
  overscrollBehavior: "none",
  scrollbarWidth: "thin",
  scrollbarColor: `${theme.palette.borderColor.dark} ${theme.palette.background.default}`,
  "&::-webkit-scrollbar": { width: 4, height: 4 },
  "&::-webkit-scrollbar-track": { background: theme.palette.background.default },
  "&::-webkit-scrollbar-thumb": { background: theme.palette.borderColor.dark, borderRadius: 2 },
}));

export const GridContent = styled(Box)(() => ({
  width: CELL_WIDTH * 24,
  height: ROW_HEIGHT * 7,
  position: "relative",
}));

export const GridDayRow = styled(Box)(({ theme, $isToday }) => ({
  position: "absolute",
  left: 0,
  width: CELL_WIDTH * 24,
  height: ROW_HEIGHT,
  backgroundColor: $isToday ? theme.palette.background.secondary : "transparent",
  borderBottom: `1px solid ${theme.palette.borderColor.default}`,
}));

export const HourLine = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  width: 1,
  height: "100%",
  backgroundColor: theme.palette.borderColor.default,
  opacity: 0.5,
}));

// ─── Show block ────────────────────────────────────────────────────────────────
export const ShowBlock = styled(Box)(({ $color, $isSelected }) => ({
  position: "absolute",
  top: 5,
  height: ROW_HEIGHT - 10,
  borderRadius: 4,
  cursor: "pointer",
  overflow: "hidden",
  transition: "background 0.15s, border-color 0.15s, box-shadow 0.15s",
  background: $isSelected
    ? `linear-gradient(135deg, ${$color}dd, ${$color}99)`
    : "transparent",
  border: `1.5px solid ${$isSelected ? $color + "cc" : $color + "30"}`,
  boxShadow: $isSelected ? `0 0 10px ${$color}44` : "none",
  "&:hover": {
    background: `linear-gradient(135deg, ${$color}55, ${$color}33)`,
    borderColor: `${$color}66`,
  },
}));

export const ShowInner = styled(Box)(() => ({
  padding: "4px 8px",
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
}));


// ─── Current-time needle ───────────────────────────────────────────────────────
export const TimeNeedle = styled(Box)(({ theme }) => ({
  position: "absolute",
  width: 2,
  height: ROW_HEIGHT,
  background: `linear-gradient(180deg, ${theme.palette.error.main}, ${theme.palette.error.main}66)`,
  zIndex: 10,
  pointerEvents: "none",
}));

export const TimeNeedleDot = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: "50%",
  transform: "translateX(-50%)",
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: theme.palette.error.main,
  boxShadow: `0 0 6px ${theme.palette.error.main}`,
}));

// ─── Hover popover ─────────────────────────────────────────────────────────────
export const PopoverPaper = styled(Paper)(({ theme }) => ({
  padding: 12,
  maxWidth: 240,
  border: `1px solid ${theme.palette.borderColor.dark}`,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[6],
}));

export const StyledPopper = styled(Popper)(() => ({
  zIndex: 1300,
}));

export const HourLabel = styled(Typography)(({ theme, $active }) => ({
  fontSize: 12,
  letterSpacing: "0.04em",
  color: $active ? theme.palette.primary.main : theme.palette.text.secondary,
}));

export const DayAbbr = styled(Typography)(({ theme, $isToday }) => ({
  fontSize: 11,
  letterSpacing: "0.12em",
  color: $isToday ? theme.palette.primary.main : theme.palette.text.secondary,
}));

export const DayDate = styled(Typography)(({ theme, $isToday }) => ({
  fontSize: 9,
  letterSpacing: "0.08em",
  color: $isToday ? theme.palette.primary.main : theme.palette.text.disabled,
  opacity: $isToday ? 0.8 : 0.6,
}));

export const ShowName = styled(Typography)(({ theme, $selected, $fontSize }) => ({
  fontSize: $fontSize,
  color: $selected ? "#fff" : theme.palette.text.primary,
  lineHeight: 1.2,
}));

export const ShowTime = styled(Typography)(({ theme, $selected }) => ({
  fontSize: 9,
  color: $selected ? "rgba(255,255,255,0.85)" : theme.palette.text.disabled,
  letterSpacing: "0.06em",
}));

export const PopoverTitle = styled(Typography)(() => ({
  marginBottom: 4,
}));

export const PopoverDesc = styled(Typography)(() => ({
  display: "block",
  marginBottom: 8,
  lineHeight: 1.5,
}));

export const PopoverTime = styled(Typography)(() => ({
  letterSpacing: "0.06em",
}));
